import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { DemoBadge, Empty, ErrorBox, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { EntitlementRow } from "@/pages/customer/Home";

const REL = { SELF: "Cardholder", PARENT: "Parents", SPOUSE: "Spouse", CHILD: "Children" };

function InsuranceCard({ ins, onChange }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/customer/insurance/${ins.id}/submit`); toast.success("Enrollment submitted for insurer review"); onChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface space-y-3 p-5" data-testid={`insurance-${ins.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2"><ShieldCheck className="text-blue-600" size={20} /><p className="font-bold">{ins.benefit_name}</p></div>
        <div className="flex gap-1">{ins.is_demo && <DemoBadge />}<StatusBadge status={ins.status} testId="insurance-status" /></div>
      </div>
      <p className="text-xs text-slate-500">Insured member: <span className="font-semibold text-slate-700">{ins.insured_member}</span> (cardholder only, unless the insurer confirms otherwise)</p>
      {ins.status === "CONFIRMED" ? (
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="insurance-coverage">
          <div><p className="opacity-60">Policy</p><p className="font-mono font-semibold">{ins.policy_reference}</p></div>
          <div><p className="opacity-60">Coverage</p><p className="font-semibold">{fmtDate(ins.coverage_start)} – {fmtDate(ins.coverage_end)}</p></div>
          {ins.coverage_details && <p className="col-span-2">{ins.coverage_details}</p>}
        </div>
      ) : (
        <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">Coverage is not active. It is displayed only after verification by the insurer or an authorized GIA administrator.</p>
      )}
      {ins.status === "NOT_SUBMITTED" && !ins.is_demo && (
        <button className="btn-secondary w-full" onClick={submit} disabled={busy} data-testid="insurance-submit-button">Submit enrollment request</button>
      )}
    </div>
  );
}

export default function Benefits() {
  const ents = useFetch("/customer/entitlements");
  const ins = useFetch("/customer/insurance");
  const services = (ents.data || []).filter((e) => e.benefit_type !== "INSURANCE");
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Available benefits" title="Your benefits" sub="Based on your activated packages" />
      {ents.loading && <Spinner />}
      <ErrorBox error={ents.error} />
      {ents.data?.length === 0 && <Empty title="No benefits yet" hint="Benefits appear after your package is activated." />}
      {services.map((e) => (
        <div key={e.id} className="surface space-y-3 p-5" data-testid={`benefit-card-${e.benefit_code}`}>
          <div className="flex items-center gap-2">{e.is_demo && <DemoBadge />}{e.expired && <StatusBadge status="EXPIRED" />}</div>
          <EntitlementRow e={e} />
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <Users size={13} /> {e.eligible_relationships.map((r) => REL[r]).join(", ")}
            <span>· Valid until {fmtDate(e.valid_until)}</span>
            {e.pending_requests > 0 && <span className="font-semibold text-purple-700">· {e.pending_requests} pending</span>}
          </div>
          {e.requires_booking && !e.expired && (e.remaining_qty == null || e.remaining_qty - e.pending_requests > 0) && (
            <Link to={`/app/book?benefit=${e.id}`} className="btn-primary btn-sm w-full" data-testid={`book-benefit-${e.benefit_code}`}><CalendarPlus size={14} /> Book</Link>
          )}
        </div>
      ))}
      {ins.data?.map((i) => <InsuranceCard key={i.id} ins={i} onChange={ins.reload} />)}
    </div>
  );
}
